import { useCallback, useEffect, useId, useRef } from 'react';
import { MdClose } from 'react-icons/md';
import IconButton from './IconButton';

const FOCUSABILI = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Guscio condiviso da Modal e Drawer: ruolo dialog, trappola del focus, Esc,
// ripristino del focus alla chiusura e blocco dello scorrimento sotto.
// `testa={false}`: niente intestazione bianca né sfondo scurito, li disegna chi
// la usa (il visore delle foto, nero e a tutto schermo come su WhatsApp).
function DialogShell({ open, onClose, label, description, confermaChiusura, children, contenitoreClass, pannelloClass, testa = true }) {
  const pannello = useRef(null);
  const precedente = useRef(null);
  const titoloId = useId();
  const descId = useId();

  const chiudi = useCallback(() => {
    if (confermaChiusura && !window.confirm('Ci sono modifiche non salvate. Vuoi chiudere comunque?')) return;
    onClose();
  }, [confermaChiusura, onClose]);

  // `chiudi` cambia a ogni render (onClose è quasi sempre una funzione inline).
  // Tenerlo in un ref evita che l'effetto qui sotto rigiri e rimetta il focus
  // sul primo campo a ogni lettera digitata.
  const chiudiRef = useRef(chiudi);
  chiudiRef.current = chiudi;

  useEffect(() => {
    if (!open) return undefined;
    precedente.current = document.activeElement;
    const scorrimento = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const nodo = pannello.current;
    const primo = nodo?.querySelector('[data-autofocus]') || nodo?.querySelector(FOCUSABILI) || nodo;
    primo?.focus?.();

    const suTasto = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); chiudiRef.current(); return; }
      if (e.key !== 'Tab' || !nodo) return;
      const elementi = [...nodo.querySelectorAll(FOCUSABILI)].filter(el => el.offsetParent !== null);
      if (!elementi.length) return;
      const inizio = elementi[0], fine = elementi[elementi.length - 1];
      if (e.shiftKey && document.activeElement === inizio) { e.preventDefault(); fine.focus(); }
      else if (!e.shiftKey && document.activeElement === fine) { e.preventDefault(); inizio.focus(); }
    };
    document.addEventListener('keydown', suTasto, true);
    return () => {
      document.removeEventListener('keydown', suTasto, true);
      document.body.style.overflow = scorrimento;
      precedente.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className={`fixed inset-0 z-50 flex ${contenitoreClass}`}>
      {/* Lo sfondo scurito isola il contenuto: cliccandolo si chiude. */}
      {testa && <div className="absolute inset-0 bg-black/50" onClick={chiudi} aria-hidden />}
      <div
        ref={pannello}
        role="dialog"
        aria-modal="true"
        aria-labelledby={testa ? titoloId : undefined}
        aria-label={testa ? undefined : label}
        aria-describedby={testa && description ? descId : undefined}
        tabIndex={-1}
        className={`relative flex flex-col outline-none ${testa ? 'bg-surface shadow-pop' : ''} ${pannelloClass}`}
      >
        {testa && <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <h2 id={titoloId} className="truncate text-base font-semibold text-ink">{label}</h2>
            {description && <p id={descId} className="mt-0.5 text-xs text-muted">{description}</p>}
          </div>
          <IconButton icon={MdClose} label="Chiudi" onClick={chiudi} className="-mr-2 -mt-1 shrink-0" />
        </div>}
        {children}
      </div>
    </div>
  );
}

const LARGHEZZE = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' };

// `ridimensionabile`: dal computer la finestra si allarga trascinando l'angolo
// in basso a destra («bastava poter allargare il form», 29/09/2026).
export function Modal({ size = 'md', ridimensionabile = false, ...resto }) {
  // Classi scritte per intero: Tailwind non genera quelle composte al volo.
  const iniziale = { sm: 'sm:w-[24rem]', md: 'sm:w-[32rem]', lg: 'sm:w-[42rem]', xl: 'sm:w-[56rem]' }[size];
  const allargabile = ridimensionabile
    // overflow-hidden, non auto: così scorre il contenuto (DialogBody) e non la
    // cornice, e il fondo della finestra si raggiunge sempre (29/09/2026).
    ? `sm:resize sm:overflow-hidden sm:min-w-[20rem] sm:max-w-[96vw] sm:max-h-[96dvh] ${iniziale}` : LARGHEZZE[size];
  return (
    <DialogShell
      {...resto}
      contenitoreClass="items-end justify-center sm:items-center sm:p-4"
      pannelloClass={`w-full max-h-[92dvh] rounded-t-lg sm:rounded-lg ${allargabile}`}
    />
  );
}

// Tutto lo schermo, per guardare foto e documenti senza lasciare l'app.
export function FullScreen({ pannelloClass = '', ...resto }) {
  return (
    <DialogShell
      {...resto}
      contenitoreClass=""
      pannelloClass={`h-full w-full ${pannelloClass}`}
    />
  );
}

export function Drawer({ ...resto }) {
  return (
    <DialogShell
      {...resto}
      contenitoreClass="justify-end"
      pannelloClass="h-full w-full max-w-md border-l border-line"
    />
  );
}

export function DialogBody({ className = '', children }) {
  return <div className={`min-h-0 flex-1 overflow-y-auto px-4 py-4 ${className}`}>{children}</div>;
}

export function DialogFooter({ children }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-4 py-3">
      {children}
    </div>
  );
}

export default Modal;
